/**
 * Il punteggio dell'avversario gia' scelto, nella variante e nella cadenza che
 * il modulo ha in quel momento.
 *
 * Finche' la lista dei collegati e' aperta il problema non esiste: e' lei a
 * chiedere `online:list` col bucket e a timbrare le righe. Ma nella Sfida
 * diretta, appena si sceglie qualcuno, la lista SI SMONTA — e con lei sparisce
 * l'unica cosa che rinfrescava i numeri. Da quel momento `selectedUser` e'
 * un'istantanea: cambiando variante o cadenza due menu piu' sotto, il suo
 * `rating` resta quello della colonna di prima. Peggio ancora quando il foglio
 * si apre gia' puntato su qualcuno (da «Chi e' collegato», dalla dashboard,
 * dalla home): la lista non si monta mai, e quel numero e' stato letto col
 * bucket di un'ALTRA schermata.
 *
 * Questo gancio e' il rinfresco che manca. Non e' il ciclo della lista in
 * miniatura: quello ha stato di caricamento, codici d'errore, conto alla
 * rovescia, contatori e polling a trenta secondi. Qui serve un numero per un
 * utente, chiesto al piu' una volta per bucket.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOnlineStore, type OnlineUser } from '../store/online-store';
import { getOnlineUsers, subscribeSocketConnected } from '../services/socket';
import { ratingBucketKey, type RatingBucket } from '../utils/rating-bucket';
import { timbraPunteggi } from '../utils/timbro-punteggi';
import { isDisplayableRating } from '../utils/rating';
import { RITARDO_CAMBIO_BUCKET_MS } from '../constants/timing';
import { log } from '../utils/logger';

/**
 * @param avversario Chi e' stato scelto. `null` = nessuno, e il gancio dorme.
 * @param bucket     Di quale punteggio si parla: variante + cadenza.
 * @param attivo     Se questo gancio e' il responsabile del rinfresco. Chi lo
 *                   usa deve spegnerlo quando la lista dei collegati e' montata,
 *                   altrimenti in due chiedono la stessa cosa nello stesso
 *                   momento.
 * @returns Il punteggio da mostrare, o `null` quando non c'e' NIENTE da
 *          mostrare: avversario senza punteggio, oppure — ed e' il caso
 *          importante — punteggio letto per un bucket diverso da quello
 *          chiesto adesso.
 */
export function usePunteggioAvversario(
  avversario: OnlineUser | null,
  bucket: RatingBucket,
  attivo: boolean,
): number | null {
  /* Per selettore, non con `getState()`: il negozio viene mockato nei test dei
     componenti e quel mock e' solo-selettore. Solo le due voci che servono —
     `setCounts` non si tocca, qui non c'e' nessun badge di totale da aggiornare
     e nei mock spesso non c'e' proprio. */
  const onlineUsers = useOnlineStore((s) => s.onlineUsers);
  const setOnlineUsers = useOnlineStore((s) => s.setOnlineUsers);

  /* La chiave come stringa: una dipendenza di effetto che non cambia a ogni
     render solo perche' chi ospita ha ricostruito l'oggetto bucket. */
  const chiave = ratingBucketKey(bucket);
  const bucketRef = useRef(bucket);
  bucketRef.current = bucket;

  const userId = avversario?.userId ?? null;

  const riga = useMemo(
    () => (userId ? onlineUsers.find((u) => u.userId === userId) : undefined),
    [onlineUsers, userId],
  );

  /*
   * UGUAGLIANZA ESATTA, non `!== undefined`.
   *
   * La lista si accontenta della seconda perche' il suo negozio viene
   * sostituito in blocco dalla risposta per il bucket che sta mostrando:
   * qualunque timbro ci sia, e' quello giusto. Qui no. Il negozio puo'
   * contenere righe timbrate da un'altra schermata (il foglio aperto gia'
   * puntato su qualcuno), oppure dal bucket di un attimo fa mentre il ricarico
   * e' ancora in volo. Meglio nessun numero per mezzo secondo che il numero di
   * un'altra variante accanto a un nome.
   */
  const fresca = riga !== undefined && riga.ratingBucket === chiave;

  /** Un giro in piu' quando il socket torna: vedi l'effetto di riaggancio. */
  const [giro, setGiro] = useState(0);

  const chiedi = useCallback(async () => {
    try {
      const risposta = await getOnlineUsers(bucketRef.current);
      setOnlineUsers(timbraPunteggi(risposta.users, risposta.bucket));
    } catch (err) {
      /* Un guasto non e' una risposta, e non si riprova a raffica: il ritorno
         del socket, qui sotto, e' cio' che rimette in moto la lettura. */
      log.warn('PunteggioAvversario', 'lettura punteggi fallita', { err: String(err) });
    }
  }, [setOnlineUsers]);

  /*
   * Una richiesta per ogni transizione, non una al secondo.
   *
   * Non serve nessun freno esplicito, e uno ci sarebbe costato caro: le
   * dipendenze cambiano solo quando cambia qualcosa che PUO' cambiare la
   * risposta — il bucket, l'avversario, la freschezza. Se l'avversario nel
   * frattempo si e' scollegato la risposta non conterra' mai la sua riga,
   * `fresca` resta falsa, nessuna dipendenza si muove e l'effetto non riparte:
   * la riscrittura del negozio da sola non lo risveglia.
   *
   * Un freno del tipo «per questo bucket ho gia' chiesto» avrebbe invece
   * zittito l'unico caso in cui rileggere serve davvero: il broadcast
   * `online:update` rimette in lista una riga SENZA timbro, e li' il numero
   * deve tornare, non sparire per sempre.
   */
  useEffect(() => {
    if (!attivo || !userId) return;
    // Il negozio ha gia' il numero giusto — il caso di chi ha appena scelto
    // dalla lista aperta. Zero richieste.
    if (fresca) return;

    const t = setTimeout(() => { void chiedi(); }, RITARDO_CAMBIO_BUCKET_MS);
    return () => clearTimeout(t);
    /* `chiave` e' fra le dipendenze anche se il corpo legge il bucket dal ref:
       con un avversario scollegato `fresca` resta falsa in entrambi i giri, e
       senza `chiave` un cambio di variante non farebbe ripartire niente. */
  }, [attivo, userId, chiave, fresca, chiedi, giro]);

  /*
   * E appena il socket torna, si riprova.
   *
   * E' il caso della PWA che rientra dal secondo piano: la prima richiesta e'
   * fallita con `NOT_CONNECTED`, nessuna dipendenza si e' mossa, e a lista
   * chiusa non c'e' nessun intervallo da trenta secondi che ci riprovi. Senza
   * questo il numero non tornerebbe mai.
   */
  useEffect(() => {
    if (!attivo) return;
    return subscribeSocketConnected(() => setGiro((n) => n + 1));
  }, [attivo]);

  if (!attivo || !fresca || riga === undefined) return null;
  return isDisplayableRating(riga.rating) ? riga.rating : null;
}
